import {ReactElement, useRef} from 'react';

import {Stack} from '@mantine/core';

import {useContacts} from '../hooks/useContacts';

import {
    MantineDataTable,
    SimpleTableBatchAction,
} from '@flexinet/ui-components';
import {useContactsSelection} from '../hooks/useContactsSelection';
import {Contact, ContactStatus} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useNavigate} from "react-router-dom";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {DataTableColumn} from "mantine-datatable";
import {addToSelection} from '../reducers';
import {useAppDispatch} from '../../../hooks/useTypedReactRedux';
import {DataTableTokenPagination} from '../../../components/DataTableTokenPagination';
import {useContactsTableColumns} from '../hooks/useContactsTableColumns.tsx';

export const RecordsTable = (
    {
        statusFilter,
        extraTools,
        settings,
    }: {
        statusFilter?: ContactStatus | null,
        extraTools?: ReactElement[],
        settings?: { label: string, onClick: () => void }[]
    }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const {user} = useAuth();

    if (!user) {
        throw new Error('user is required parameter')
    }

    const {allowedActions} = useVerifiedPermissions();

    const {
        visibleItems,
        visibleCount,
        nextToken,
        fetchRecords,
        isFetchingNextPage,
        limit,
        handleFilterByKeyword,
        handleItemsPerPageChange,
    } = useContacts(statusFilter);

    const {columns} = useContactsTableColumns();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const {
        selection,
        handleClearSelection,
    } = useContactsSelection();

    let selectedRecords = [] as Contact[];
    if (allowedActions.includes('DeleteContact')) {
        selectedRecords = Object.keys(selection).map(key => selection[key]);
    }

    return (
        <Stack>
            <SimpleTableBatchAction
                limit={parseInt(limit)}
                handleItemsPerPageChange={handleItemsPerPageChange}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={visibleItems}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach(item => {
                        dispatch(addToSelection(item as unknown as Contact));
                    })
                }}
                onRowClick={({record}) => {
                    navigate('/contacts/' + record.id);
                }}
                fetching={false}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination
                totalCount={visibleCount.toString()}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
                loading={isFetchingNextPage}
            />
        </Stack>
    );
};
